import { useMemo } from 'react';
import { useCompany } from '@/features/settings/hooks/use-company';
import { useAvailablePlans } from '@/features/settings/hooks/use-available-plans';
import type { EarningsEstimation } from '../types/product-form.types';

interface UseProductEarningsOptions {
  price: number;
  discountPrice?: number;
}

export function useProductEarnings({ price, discountPrice }: UseProductEarningsOptions): EarningsEstimation | null {
  const { data: company } = useCompany();
  const { data: availablePlans } = useAvailablePlans();

  return useMemo(() => {
    const effectivePrice = discountPrice || price;
    if (!effectivePrice || effectivePrice <= 0) return null;

    const commissionRate = company?.platformFeePercent ?? 10;
    const commissionAmount = Math.round((effectivePrice * commissionRate) / 100);
    const netAmount = effectivePrice - commissionAmount;

    // Find current plan and better plans
    const currentPlanName = company?.subscribe?.plan?.name;
    const betterPlans = availablePlans
      ?.filter((plan) => plan.commissionRate < commissionRate && plan.isEnabled)
      .sort((a, b) => b.commissionRate - a.commissionRate)
      .slice(0, 2)
      .map((plan) => ({
        name: plan.name,
        commissionRate: plan.commissionRate,
        netAmount: effectivePrice - Math.round((effectivePrice * plan.commissionRate) / 100),
        savings: Math.round((effectivePrice * (commissionRate - plan.commissionRate)) / 100),
      }));

    return {
      price: effectivePrice,
      commissionRate,
      commissionAmount,
      netAmount,
      currentPlanName,
      betterPlans: betterPlans || [],
    };
  }, [price, discountPrice, company, availablePlans]);
}
